import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, List, ListItem, 
         ListItemText, ListItemAvatar, Avatar, Checkbox, Divider, 
         CircularProgress } from '@mui/material';
import { useChat } from './ChatContext';
import axios from 'axios';
import Cookies from 'js-cookie';

const GroupChatControl = ({ onGroupCreated, onClose }) => {
  const { createGroupChat } = useChat();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch available users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('token');
      const response = await axios.get(
        `${process.env.REACT_APP_STATUS_API_URL}/admin/list_users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle user selection
  const handleToggleUser = (username) => {
    setSelectedUsers(prev => 
      prev.includes(username) 
        ? prev.filter(u => u !== username) 
        : [...prev, username]
    );
  };

  // Create group chat
  const handleCreateGroup = async () => {
    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('Please select at least one participant');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      
      const newGroup = await createGroupChat({
        name: name.trim(),
        description: description.trim(),
        participants: selectedUsers
      });
      
      if (newGroup) {
        // Call the callback with the new group
        if (onGroupCreated) {
          onGroupCreated(newGroup);
        }
      }
    } catch (error) {
      console.error('Failed to create group:', error);
      setError(`Failed to create group: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#e0e0e0' }}>
        Create New Group Chat
      </Typography>
      
      {/* Group info fields */}
      <TextField
        fullWidth
        label="Group Name"
        variant="outlined"
        margin="normal"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ 
          mb: 2,
          '.MuiOutlinedInput-root': {
            bgcolor: '#363636',
            color: '#e0e0e0',
          },
          '.MuiInputLabel-root': {
            color: '#9e9e9e'
          }
        }}
      />
      
      <TextField
        fullWidth
        label="Description (optional)"
        variant="outlined"
        margin="normal"
        multiline
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ 
          mb: 2,
          '.MuiOutlinedInput-root': {
            bgcolor: '#363636',
            color: '#e0e0e0',
          },
          '.MuiInputLabel-root': {
            color: '#9e9e9e'
          }
        }}
      />
      
      {/* Participants section */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#9e9e9e' }}>
        Select Participants
      </Typography>
      
      {/* User list */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        bgcolor: '#252526',
        borderRadius: 1,
        mb: 2
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={24} sx={{ color: '#4a9bfc' }} />
          </Box>
        ) : (
          <List disablePadding>
            {users.map(user => (
              <React.Fragment key={user.username}>
                <ListItem
                  dense
                  button
                  onClick={() => handleToggleUser(user.username)}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedUsers.includes(user.username)}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      color: '#9e9e9e',
                      '&.Mui-checked': { color: '#4a9bfc' }
                    }}
                  />
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#4a9bfc' }}>
                      {user.fornavn?.[0]}{user.etternavn?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${user.fornavn || ''} ${user.etternavn || ''}`}
                    secondary={user.username}
                    primaryTypographyProps={{ color: '#e0e0e0', noWrap: true }}
                    secondaryTypographyProps={{ color: '#9e9e9e', noWrap: true }}
                  />
                </ListItem>
                <Divider component="li" sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)' }} />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
      
      {/* Error message */}
      {error && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#ff5252', 
            mb: 2, 
            textAlign: 'center' 
          }}
        >
          {error}
        </Typography>
      )}
      
      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: '#9e9e9e',
            borderColor: '#9e9e9e',
            '&:hover': {
              borderColor: '#e0e0e0',
              color: '#e0e0e0'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateGroup}
          disabled={!name.trim() || selectedUsers.length === 0 || creating}
          sx={{
            bgcolor: '#4a9bfc',
            '&:hover': {
              bgcolor: '#3a8bec'
            },
            '&.Mui-disabled': {
              bgcolor: '#252526'
            }
          }}
        >
          {creating ? (
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          ) : (
            'Create Group'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default GroupChatControl;
